import styled from 'styled-components';
import LocationTime from '@rsos/components/capstone/DataCards/DataCardLocation/LocationTime';
import CallSource from '@rsos/components/capstone/Location/CallSource';
import { Box, Text } from '@rsos/components/capstone/base';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';

const StyledCallerIdMain = styled.span<{ $isClickable?: boolean }>`
  text-decoration: none;
`;

const StyledCallerIdRow = styled(Box)<{ $isClickable?: boolean }>`
  ${({ $isClickable }) =>
    $isClickable
      ? `
    cursor: pointer;
  `
      : ''}
`;

export const StyledCallerID = styled(Text).attrs({
  color: 'panePrimaryText',
  fontWeight: 'bold',
})<{ $isClickable?: boolean }>`
  display: inline-flex;
  align-items: baseline;
  text-decoration: none;
`;

interface Props {
  callerID: string;
  timestamp: number;
  source: string;
  recentTranscriptionsCount?: number;
  onCallerIdClick?: () => void;
}

export const ExpandedHeaderData = ({
  callerID,
  timestamp,
  source,
  recentTranscriptionsCount = 0,
  onCallerIdClick,
}: Props) => {
  const hasLocationsToRender = useHasLocationsToRender();
  const canOpenRecentTranscripts =
    recentTranscriptionsCount > 0 && typeof onCallerIdClick === 'function';

  // `66` includes a 16px padding bottom. `auto` for outbound SMS
  const eventPaneHeaderHeight = hasLocationsToRender ? 66 : 'auto';

  return (
    <Box height={eventPaneHeaderHeight}>
      <StyledCallerIdRow
        horizontal
        align="center"
        $isClickable={canOpenRecentTranscripts}
        data-name={
          canOpenRecentTranscripts
            ? 'event-pane-caller-id-cta'
            : 'event-pane-caller-id'
        }
        role={canOpenRecentTranscripts ? 'button' : undefined}
        tabIndex={canOpenRecentTranscripts ? 0 : undefined}
        aria-label={
          canOpenRecentTranscripts
            ? `Open recent transcripts (${recentTranscriptionsCount})`
            : undefined
        }
        onClick={canOpenRecentTranscripts ? onCallerIdClick : undefined}
        onKeyDown={
          canOpenRecentTranscripts
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCallerIdClick?.();
                }
              }
            : undefined
        }
      >
        <StyledCallerID $isClickable={canOpenRecentTranscripts}>
          <StyledCallerIdMain $isClickable={canOpenRecentTranscripts}>
            {formatInternationalPhoneNumberHuman(callerID)}
          </StyledCallerIdMain>
        </StyledCallerID>
      </StyledCallerIdRow>
      {hasLocationsToRender && (
        <>
          <Box paddingTop={1} paddingBottom={1}>
            <LocationTime
              size="xsmall"
              timeAgoTextSize="small"
              timeBoxTextSize="xsmall"
              timeStamp={timestamp}
              noPadding
            />
          </Box>
          <CallSource source={source} />
        </>
      )}
    </Box>
  );
};
